import { NavLink, useLocation } from 'react-router';
import { Icon, type IconName } from './Icon';

export interface SectionNavItem {
  to: string;
  label: string;
  /** An icon, or a short text (initial) shown in the same slot. */
  icon?: IconName;
  avatar?: string;
  description?: string;
  badge?: string;
}

export interface SectionNavGroup {
  title?: string;
  items: SectionNavItem[];
  /** e.g. "mobile-only-block": a group shown only on phones. */
  className?: string;
}

/**
 * The middle pane on section pages: a titled list of sections, the current one highlighted. On the bare
 * base path (e.g. /settings) the first section is the one shown, so it is highlighted too.
 */
export function SectionNav({
  title,
  items,
  groups,
  footer,
  base,
  action,
}: {
  title: string;
  items?: SectionNavItem[];
  groups?: SectionNavGroup[];
  footer?: React.ReactNode;
  base?: string;
  /** A small control next to the title (e.g. a "new" button). */
  action?: React.ReactNode;
}) {
  const { pathname } = useLocation();
  const onBase = base !== undefined && pathname.replace(/\/$/, '') === base;
  const all = groups ?? [{ items: items ?? [] }];
  const first = all.find((g) => g.items.length > 0)?.items[0]?.to;
  return (
    <nav className="section-nav" aria-label={title}>
      <div className="section-nav-head">
        <div className="section-nav-title">{title}</div>
        {action}
      </div>
      {all.map((group, gi) => (
        <div key={gi} className={`section-nav-group${group.className ? ` ${group.className}` : ''}`}>
          {group.title && <div className="section-nav-group-title">{group.title}</div>}
          <ul className="plain-list">
            {group.items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end
                  className={({ isActive }) => `section-nav-link${isActive || (onBase && item.to === first) ? ' active' : ''}`}
                >
                  <span className={`section-nav-icon${item.avatar ? ' is-avatar' : ''}`}>
                    {item.avatar ? item.avatar : item.icon ? <Icon name={item.icon} size={18} /> : null}
                  </span>
                  <span className="section-nav-text">
                    <span className="truncate">{item.label}</span>
                    {item.description && <span className="section-nav-desc">{item.description}</span>}
                  </span>
                  {item.badge && <span className="badge">{item.badge}</span>}
                  <span className="section-nav-chevron mobile-only">
                    <Icon name="chevron" size={16} />
                  </span>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {footer && <div className="section-nav-footer">{footer}</div>}
    </nav>
  );
}

/** Page heading for a section: title, optional description, optional actions on the right. */
export function PageHeader({ title, description, back, actions }: { title: string; description?: string; back?: () => void; actions?: React.ReactNode }) {
  return (
    <header className="page-header">
      {back && (
        <button className="icon-btn mobile-only" type="button" onClick={back}>
          <Icon name="back" label="Back" />
        </button>
      )}
      <div className="page-header-text">
        <h1>{title}</h1>
        {description && <p className="muted">{description}</p>}
      </div>
      {actions && <div className="page-header-actions">{actions}</div>}
    </header>
  );
}

/** A calm, centred empty state for a whole page (like the chat splash, smaller). */
export function PageEmpty({ icon, title, body, action }: { icon: IconName; title: string; body?: string; action?: React.ReactNode }) {
  return (
    <div className="page-empty">
      <div className="page-empty-mark" aria-hidden>
        <Icon name={icon} size={30} />
      </div>
      <h2>{title}</h2>
      {body && <p className="muted">{body}</p>}
      {action}
    </div>
  );
}
